import {
  AmenitiesDescription,
  AmenitiesDescriptionContainer,
  CompactTitle,
  Container,
  ListContainer,
  ReadMoreButton,
  ReadMoreContainer,
  Title,
} from './styles';
import React from 'react';

import { Amenity } from './amenity';
import { CollapsableBlock } from 'components/collapsable-block';
import { ResourceFeature } from 'store/transactions/types';
import { useAmenities } from './use-amenities.hook';
import { useIntl } from 'react-intl';

interface AmenitiesProps {
  amenities: Array<ResourceFeature>;
  amenitiesDescription?: string;
  isMobileDevice?: boolean;
  // RES-999: routed booking-details page embeds this inside its own card and wants this
  // section's heading to match the card's other small grey labels. Defaults to false so every
  // other caller is unaffected.
  compact?: boolean;
  /*
   * RES-1367: folds this list behind the same collapsible heading "Additional information" uses,
   * so the booking-details card reads as a matching pair of sections rather than one block that
   * opens and one that cannot. The block supplies the heading, so this component's own title is
   * left out rather than printed above it a second time.
   *
   * Defaults false: everywhere else this list IS the section, and collapsing it by default would
   * hide the amenities a reader came to that page to read.
   */
  collapsible?: boolean;
}

export const Amenities = ({
  amenities,
  amenitiesDescription,
  isMobileDevice,
  compact = false,
  collapsible = false,
}: AmenitiesProps): JSX.Element => {
  const intl = useIntl();
  const { isExpanded, isOverflowing, onExpand, setDescriptionNode } = useAmenities(isMobileDevice);
  const TitleComponent = compact ? CompactTitle : Title;
  /*
   * ⭐ TWO NAMES, ON PURPOSE. The collapsible variant is the booking-details card (RES-1367), where
   * this block sits beside "Additional information" and describes a room the tenant has ALREADY
   * booked -- "More about this space" is the friendlier question it is actually answering there.
   * Everywhere else this list is a feature inventory on a room the tenant is still choosing, and
   * "Amenities" is the right word for that, so the shared key is left alone.
   */
  const title = intl.formatMessage({ id: collapsible ? 'more_about_this_space' : 'amenities' });

  const body = (
    <>
      {/* RES-1367: only when there is something to describe. Most resources carry no description,
          and this box was being rendered empty for them -- zero-height, but a real child of the
          surrounding flex column, so it still drew that column's gap above the amenity list. */}
      {!!amenitiesDescription && (
        <AmenitiesDescriptionContainer>
          <AmenitiesDescription ref={setDescriptionNode} isOverflowing={isOverflowing} isExpanded={isExpanded}>
            {amenitiesDescription}
          </AmenitiesDescription>
          {isOverflowing && (
            <ReadMoreContainer>
              <ReadMoreButton onClick={onExpand}>
                {isExpanded ? intl.formatMessage({ id: 'read_less' }) : intl.formatMessage({ id: 'read_more' })}
              </ReadMoreButton>
            </ReadMoreContainer>
          )}
        </AmenitiesDescriptionContainer>
      )}
      <ListContainer withLeadIn={!collapsible && !!amenitiesDescription}>
        {[...amenities]
          .sort((a, b) => {
            const ctaTypeA = a.cta_type ? 1 : 0;
            const ctaTypeB = b.cta_type ? 1 : 0;

            return ctaTypeB - ctaTypeA;
          })
          .map(amenity => (
            <Amenity key={amenity.id} amenity={amenity} />
          ))}
      </ListContainer>
    </>
  );

  /*
   * `section`: the bold heading stays, the block's own top border and lead-in go -- the card it
   * sits in already draws both. Same variant the sibling "Additional information" block uses.
   *
   * The block is returned on its own, without this component's Container: that wrapper's -12px
   * bleed is sized for the plain receipt page, and inside a card it pulls the section out of line
   * with every row above it.
   */
  if (collapsible) {
    return (
      <CollapsableBlock title={title} section>
        {body}
      </CollapsableBlock>
    );
  }

  return (
    <Container compact={compact}>
      <TitleComponent>{title}</TitleComponent>
      {body}
    </Container>
  );
};
